import React from 'react';

import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';

import { addAlert } from 'store/actions/alert/alerts';
import {
  addFeaturesToLayerUsingGeojsonGeometry,
  clearMapLayer,
  createLayer,
} from 'utils/map/layers';
import {
  addLayerToMap,
  getAllClickedFeatures,
  getAllFeaturesByDetail,
  removeComponentLayers,
  zoomToGeojsonGeometryWithBuffer,
} from 'utils/map/map';
import { authDelRequestWithParam } from 'helpers/apiRequests';
import { hasObjectKeys } from 'utils/main';
import { removeLocalZone } from 'store/actions/map/allZones';
import CustomizedTable from 'components/common/components/tablev2/CustomizedTable';
import Filters from 'components/common/components/filters/Filtersv2';
import useStyles from 'consts/styles/RightScreenPart';
import CircleButton from 'components/common/components/CircleButton';
import Icons from 'consts/icons';
import { filterZonesByFilterObject } from 'utils/filters';
import PermissionsGate from 'components/common/PermissionGate';
import { SCOPES } from 'consts/permission-maps';
import { SidebarMenuDetailsUseStyles } from 'components/sidebar/SidebarMenuDetailsUseStyles';
import { userStyles } from 'consts/styles/User';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import { AddZoneForm, EditZone } from './private';
import DisplayZoneDetails from './DisplayZoneDetails';
import { changeObjectHeightUnit } from './utils/ZonesListUtils';
import {
  addPrivateZonesToLayer,
  addZonesToLayer,
} from './utils/zoneDetailsUtils';

const PrivateZonesList = props => {
  const { zonesType, heightUnit } = props;

  const subtitles = useSelector(state => state.language).alerts;
  const closeSubtitles = useSelector(state => state.language).close_;
  const map = useSelector(state => state.map);
  const allZones = useSelector(state =>
    zonesType === 'public' ? state.allZones : state.allLocalZones
  );

  const [openModal, setOpenModal] = React.useState(false);
  const [zoneToEdit, setZoneToEdit] = React.useState(null);
  const [totalRows, setTotalRows] = React.useState(0);
  const [zones, setZones] = React.useState([]);
  const [idItemOnClick, setIdItemOnClick] = React.useState(null);
  const [selectedZone, setSelectedZone] = React.useState(null);
  const [filtersDataObject, setFiltersDataObject] = React.useState(null);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [currentPage, setCurrentPage] = React.useState(0);
  const flattedZones = React.useRef([]);
  const layer = React.useRef(null);
  const dropdownOptionsToSpecifyKeyValuesToFilter = {
    type:
      zonesType === 'public'
        ? Object.keys(allZones)
        : Object.keys(allZones).map(v => v.slice(0, -6)),
  };
  const filtersType = ['name', 'type', 'minHeight', 'maxHeight'];

  const classes = useStyles();
  const classesSidebar = SidebarMenuDetailsUseStyles();
  const classesUser = userStyles();

  const dispatch = useDispatch();

  const initMap = () => {
    if (!hasObjectKeys(map)) return;
    addLayerToMap(map, layer.current);
    map.on('click', handleClick);
    return () => cleanup();
  };

  React.useEffect(() => {
    layer.current = createLayer('zonesLayer');
  }, []);

  React.useEffect(initMap, [map]);

  React.useEffect(() => {
    handleZonesChange();
  }, [allZones]);

  React.useEffect(() => {
    if (!selectedZone) return handleAllObjectVisible();
    selectRow(selectedZone);
  }, [zones]);

  React.useEffect(() => {
    handleFilterZones();
  }, [filtersDataObject, rowsPerPage, currentPage, heightUnit]);

  React.useEffect(() => {
    if (!selectedZone) {
      clearMapLayer(layer.current);
      if (!selectedZone) return handleAllObjectVisible();
    }
  }, [selectedZone]);

  const handleClick = event => {
    setIdItemOnClick(
      getAllFeaturesByDetail(
        getAllClickedFeatures(map, event),
        'zone'
      )[0]?.id_.substring(0, 36)
    );
  };

  React.useEffect(() => {
    if (idItemOnClick !== null) {
      setSelectedZone(zones.find(zone => zone.uid === idItemOnClick));
    }
    return () => setIdItemOnClick(null);
  }, [idItemOnClick]);

  const cleanup = () => {
    map.un('click', handleClick);
    removeComponentLayers(map);
  };

  const handleAllObjectVisible = () => {
    if (zones && zones?.length) {
      if (typeof zones[0]?.geojson === 'string') {
        addZonesToLayer(layer.current, zones);
      } else addPrivateZonesToLayer(layer.current, zones);
    }
  };

  const handleZonesChange = () => {
    const tmp = Object.values(allZones).flat();
    flattedZones.current = JSON.parse(JSON.stringify(tmp));
    setTotalRows(tmp.length);
    setZones(
      changeObjectHeightUnit(
        JSON.parse(
          JSON.stringify(tmp.splice(currentPage * rowsPerPage, rowsPerPage))
        ),
        heightUnit === 'm'
      )
    );
  };

  const handleFilterZones = () => {
    if (!flattedZones.current.length) return;
    const filteredZones = filterZonesByFilterObject(
      flattedZones.current,
      filtersDataObject
    );
    setTotalRows(filteredZones.length);
    setZones(
      changeObjectHeightUnit(
        JSON.parse(
          JSON.stringify(
            filteredZones.splice(currentPage * rowsPerPage, rowsPerPage)
          )
        ),
        heightUnit === 'm'
      )
    );
  };

  const deletePrivateZone = async zoneName => {
    const foundZone = zones.find(z => z.name === zoneName);
    if (!foundZone.uid) return;
    const result = await authDelRequestWithParam('areas', foundZone.uid);

    if (result?.status === 204) {
      setSelectedZone(null);
      dispatch(removeLocalZone(foundZone));
      dispatch(
        addAlert({
          title: subtitles.deleteSuccess.title_,
          desc: subtitles.deleteSuccess.desc_,
          type: 'success',
        })
      );
    } else
      dispatch(
        addAlert({
          title: subtitles[7].title_,
          desc: subtitles[7].desc_,
          type: 'error',
        })
      );
  };

  const zoomToZone = geojson => {
    zoomToGeojsonGeometryWithBuffer(map, geojson);
  };

  const selectRow = (rowData, index) => {
    let zone;
    if (index >= 0) zone = zones[index];
    else zone = zones.find(z => z.uid === rowData.uid);
    if (!zone) return;
    setSelectedZone(zone);
    addFeaturesToLayerUsingGeojsonGeometry(layer.current, zone.geojson);
  };

  return (
    <>
      {!selectedZone ? (
        <>
          <div
            className={`${classesSidebar.SidebarMenuDetails}__table__details__filters`}
          >
            <Filters
              filters={filtersType}
              setFiltersData={setFiltersDataObject}
              dropdownOptionsToSpecifyKeyValuesToFilter={
                dropdownOptionsToSpecifyKeyValuesToFilter
              }
            />
          </div>
          <div>
            {zonesType === 'public' || zoneToEdit || openModal ? null : (
              <PermissionsGate scopes={[SCOPES.canAddZone]}>
                <CircleButton
                  position={{ right: 30, bottom: 20, position: 'absolute' }}
                  size={25}
                  PassedIcon={<Icons.Actions.Add2 />}
                  onClickFunction={() => setOpenModal(!openModal)}
                />
              </PermissionsGate>
            )}
          </div>
          <div className={`${classes.Wrapper}__history-wrapper-short`}>
            <CustomizedTable
              headers={[
                'Name',
                'Type',
                `AMSL min [${heightUnit}]`,
                `AMSL max [${heightUnit}]`,
                'Active',
              ]}
              data={zones.map(zone => ({
                name: zone.name,
                type: zone.type,
                heightMin: zone.amsl_min || zone.min,
                height: zone.amsl_max || zone.max,
                active: zone.active,
              }))}
              onClickFunction={selectRow}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              total={totalRows}
              selectedRow={selectedZone}
              filterAdd={zonesType !== 'public'}
            />
          </div>
        </>
      ) : (
        <Grid
          container
          className={`${classesUser.User}__dashboard--inline`}
        >
          <Tooltip title={closeSubtitles} placement="bottom">
            <div
              className={`${classesUser.User}__settings__close`}
              onClick={() => setSelectedZone(null)}
              onKeyPress={() => setSelectedZone(null)}
              role="button"
              tabIndex={0}
            >
              <div
                className={`${classesUser.User}__settings__close__container`}
              >
                <Icons.Actions.Close />
              </div>
            </div>
          </Tooltip>
          <DisplayZoneDetails
            zoneToDisplay={selectedZone}
            setSelectedZone={setSelectedZone}
            zoom={zoomToZone}
            setZoneToEdit={zonesType === 'private' ? setZoneToEdit : null}
            deletePrivateZone={
              zonesType === 'private' ? deletePrivateZone : null
            }
          />
        </Grid>
      )}
      {!!openModal && (
        <AddZoneForm handleCloseModal={() => setOpenModal(!openModal)} />
      )}
      {!!zoneToEdit && selectedZone && (
        <EditZone zoneToEdit={selectedZone} setOpenModal={setZoneToEdit} />
      )}
    </>
  );
};
const mapStateToProps = state => ({
  heightUnit: state.heightUnit,
});

PrivateZonesList.propTypes = {
  zonesType: PropTypes.string.isRequired,
  heightUnit: PropTypes.string.isRequired,
};

export default connect(mapStateToProps)(PrivateZonesList);
